import { Tag } from '@oneaudi/unified-web-components';
import { useCallback, useMemo, useState } from 'react';
import { useAppServices } from '~/app/contexts/app-context';
import { useI18n } from '~/app/i18n/i18n';
import { useMarketConfig } from '~/app/market-config';
import { appTrackingService } from '~/app/tracking/app-tracking-service';
import { mapTrackingIncentives } from '~/app/tracking/map-tracking-incentives';
import { Incentive } from '~/components/incentive/incentive';
import { IncentiveData } from '~/graphql/__generated__/graphql';
import { useCatalog, useConfigurator } from '~/graphql/hooks/use-configurator';
import { CatalogItemType, FeatureCategory } from '~/graphql/types';
import { DATA_TEST_ID } from '~/test/data-test-id';
import { getSubtitle, sortIncentives } from '~/utils/incentives';
import { TacticalContainer } from '../styles/tacticals-style';

export const IncentivesPopover = ({ incentives }: { incentives: IncentiveData[] }) => {
	const { carInfo } = useCatalog();
	const { changeConfiguration, isItemLoading } = useConfigurator();
	const [isOpen, setIsOpen] = useState(false);
	const { formatter, market, language } = useMarketConfig();
	const { i18n } = useI18n();
	const {
		featureServices: { oneGraphClient },
	} = useAppServices();

	const validIncentives = useMemo(() => {
		// currently we dont get the status field in the incentives on the engine level (which is required to know if they been added to the configuration), we have to match them to the general list and use them instead.
		if (carInfo?.incentives) {
			// Get all the incentives that are available for the engine
			const engineIncentivesIds = new Set(incentives.map((item) => item.id));
			const matchingIncentives = carInfo.incentives.filter((item) => engineIncentivesIds.has(item.id));
			// Sort the incentives
			return sortIncentives(matchingIncentives);
		}
	}, [carInfo?.incentives, incentives]);

	const handlePopoverClick = useCallback(
		(open: boolean) => {
			setIsOpen(open);
			const eventName = `tacticals - click to ${open ? 'open' : 'close'} all incentives layer`;

			const relatedIncentive = mapTrackingIncentives(validIncentives || []);
			appTrackingService.incentivesClickEvent({
				eventName,
				relatedIncentive,
				componentName: 'engine selection',
				elementName: 'icon',
			});
		},
		[validIncentives],
	);

	const handleIncentiveClick = useCallback(
		async (incentive: CatalogItemType['incentive']) => {
			// Tracking of the incentive select/deselect
			appTrackingService.incentivesClickEvent({
				eventName: `tacticals - click to ${incentive.status.selected ? 'deselect' : 'select'} discount`,
				componentName: 'all incentives layer',
				label: incentive.action.title || '',
				elementName: 'check box',
				relatedIncentive: mapTrackingIncentives([incentive]),
			});

			const refetchCarline = async () =>
				oneGraphClient.refetchQueries({
					include: ['Carline'],
				});

			const newIncentive = {
				...structuredClone(incentive),
				featureId: incentive.id,
				type: 'incentive' as FeatureCategory,
				name: 'incentive',
			};
			await changeConfiguration(newIncentive);
			await refetchCarline();
		},
		[changeConfiguration, validIncentives],
	);

	if (!validIncentives || validIncentives.length === 0) return null;

	return (
		<>
			<TacticalContainer>
				<div className="--trigger-container" onClick={(event) => event.stopPropagation()}>
					<Tag
						size="sm"
						variant="offer"
						label={i18n('ngc.incentives.popover.trigger')}
						data-testid={DATA_TEST_ID.TACTICAL_TRIGGER}
						popover={{
							id: DATA_TEST_ID.TACTICAL_TRIGGER,
							triggerAriaLabel: 'Info icon click',
							content: (
								<div className="--content-container">
									{validIncentives &&
										validIncentives.map((incentive) => (
											<Incentive
												key={incentive.id}
												title={incentive.action.title}
												selected={incentive.status.selected}
												select={() => handleIncentiveClick(incentive)}
												price={
													incentive.price.value
														? formatter.formatPrice(incentive.price.value)
														: undefined
												}
												subtitle={getSubtitle(
													i18n,
													incentive.action.description ?? '',
													incentive.validityPeriod.to ?? '',
													market,
													language,
												)}
												isLoading={isItemLoading({
													type: 'incentive' as FeatureCategory,
													featureId: incentive.id,
												})}
												incentive={incentive}
												onOpenTermsAndCondition={() => setIsOpen(false)}
											/>
										))}
								</div>
							),
							withTip: true,
							onClose: () => handlePopoverClick(false),
							onTriggerClick: () => handlePopoverClick(!isOpen),
							open: isOpen,
						}}
					/>
				</div>
			</TacticalContainer>
		</>
	);
};
